/**
 * SendIt MFE Integration Contract
 *
 * Defines the TypeScript types for the interface between EBProjects and the
 * evo-platform-sendit-mfe micro-frontend. Any change to the MFE's event
 * shape should be reflected here — type mismatches will surface at compile
 * time.
 *
 * Owner: EBProjects team
 * MFE: evo-platform-sendit-mfe (owned by SendIt / Platform team)
 */

/** The MFE identifier used with openMfe() */
export const SENDIT_MFE_NAME = 'evo-platform-sendit-mfe' as const;

/** Actions the MFE may fire back via CustomEvent.detail.action */
export type SendItAction = 'close' | 'dismiss' | 'cancel' | 'success';

/** Shape of CustomEvent.detail fired by the SendIt MFE callback */
export interface SendItCallbackDetail {
  action?: SendItAction;
  /** Any additional payload the MFE may include on success */
  [key: string]: unknown;
}

/** Parameters passed to openMfe() for the SendIt MFE */
export interface SendItMfeParameters {
  mode: 'single' | 'bulk';
  email: string; // JSON-stringified email payload
  callbackEventName: string;
  /**
   * When 'true' the MFE closes itself after a successful send instead of
   * showing its own "Email sent successfully" screen.
   */
  autoCloseOnSuccess?: 'true' | 'false';
  [key: string]: unknown;
}

/** The email payload JSON-stringified into the `email` parameter */
export interface SendItEmailPayload {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  attachments?: SendItEmailAttachment[];
}

export interface SendItEmailAttachment {
  type: 'base64';
  filename: string;
  mimeType: string;
  data: string;
}

/** Options object passed to openMfe() */
export interface SendItMfeOptions {
  instanceId?: string;
  parameters: Record<string, unknown>;
  type: 'modal';
}

/**
 * Type guard: validates that a CustomEvent detail matches the expected
 * SendItCallbackDetail shape. Use in event handlers to detect contract drift.
 */
export function isSendItCallbackDetail(detail: unknown): detail is SendItCallbackDetail {
  if (detail === null || detail === undefined) return true; // MFE may fire with no detail
  if (typeof detail !== 'object') return false;
  const d = detail as Record<string, unknown>;
  if ('action' in d && d.action !== undefined) {
    const validActions: SendItAction[] = ['close', 'dismiss', 'cancel', 'success'];
    return typeof d.action === 'string' && validActions.includes(d.action as SendItAction);
  }
  return true; // detail is an object but has no action — acceptable
}
